import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "components/ui/combobox";
import { MultipleFilterTrigger } from "components/NewAdvancedSearch/filters/MultipleFilterTrigger";
import { OperatorSelect } from "components/NewAdvancedSearch/filters/OperatorSelect";
import { useStickyChipScroll } from "components/NewAdvancedSearch/filters/useStickyChipScroll";

// A listFilter behaves like the tag filter — a Contains/Excludes operator over a
// multi-select — but its options come from the descriptor's async `dataFn` and
// its values are plain strings (MAC addresses, country names) rather than tag
// ids. It serializes to the backend's `search:<key>:<uuid>` envelope in
// buildQueryParams.
const OPERATIONS = [
  { symbol: "∋", text: "Contains", value: "contains" },
  { symbol: "∌", text: "Excludes", value: "excludes" },
];

const initialValue = {
  op: "contains",
  value: [],
};

export function ListFilterEditor({ descriptor, value, onChange }) {
  const { key, dataFn, placeholder } = descriptor;
  const current = value ?? initialValue;

  const { data: options = [], isFetching } = useQuery({
    queryKey: ["list-filter-options", key],
    queryFn: dataFn,
    enabled: typeof dataFn === "function",
  });

  const labelFor = id => options.find(o => o.id === id)?.name ?? id;
  const { ref: chipsRef, onScroll: onChipsScroll } = useStickyChipScroll(
    (current.value ?? []).length
  );

  const handleChangeOp = op => onChange({ op, value: current.value });
  const handleChangeValue = next => onChange({ op: current.op, value: next });

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-4">
      <OperatorSelect
        operations={OPERATIONS}
        value={current.op}
        onChange={handleChangeOp}
      />
      <Combobox
        items={options}
        multiple
        value={current.value ?? []}
        onValueChange={handleChangeValue}
        itemToStringLabel={o => o.name}
      >
        <MultipleFilterTrigger
          labelFor={labelFor}
          keyFor={id => id}
          placeholder={placeholder ?? "Select..."}
        />
        <ComboboxContent>
          <ComboboxChips
            render={<div ref={chipsRef} onScroll={onChipsScroll} />}
            className={clsx(
              !current?.value?.length && "tw:min-h-0 tw:p-0",
              "tw:max-h-44 tw:overflow-y-auto"
            )}
          >
            <ComboboxValue>
              {selected =>
                selected.map(id => (
                  <ComboboxChip
                    key={id}
                    className="tw:h-auto tw:max-w-full tw:min-w-0 tw:justify-start tw:text-left tw:whitespace-normal"
                  >
                    <span className="tw:min-w-0 tw:wrap-anywhere">
                      {labelFor(id)}
                    </span>
                  </ComboboxChip>
                ))
              }
            </ComboboxValue>
          </ComboboxChips>
          <ComboboxInput showTrigger={false} placeholder="Search" />
          <ComboboxEmpty>
            {isFetching ? "Loading…" : "No options found."}
          </ComboboxEmpty>
          <ComboboxList>
            {item => (
              <ComboboxItem key={item.id} value={item.id}>
                {item.name}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
